import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const checkboxVariants = cva(
  "peer shrink-0 rounded-sm border shadow-sm focus-visible:outline-hidden focus:outline-hidden outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ease-in-out",
  {
    variants: {
      variant: {
        default:
          "border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground hover:shadow-md",
        routine: [
          "rounded-md border-2",
          "size-5 @[576px]:size-6 min-w-5 min-h-5 @[576px]:min-w-6 @[576px]:min-h-6",
          // Unchecked state
          "border-[var(--checkbox-border-color,hsl(var(--primary)))]",
          "bg-transparent",
          // Checked state
          "data-[state=checked]:bg-[var(--checkbox-bg-color,hsl(var(--primary)))]",
          "data-[state=checked]:border-[var(--checkbox-border-checked-color,hsl(var(--primary)))]",
          "data-[state=checked]:text-white",
          // Hover states
          "hover:shadow-sm hover:scale-105",
          "hover:border-[var(--checkbox-border-hover-color,hsl(var(--primary)/0.8))]",
          "data-[state=checked]:hover:bg-[var(--checkbox-bg-hover-color,hsl(var(--primary)/0.9))]",
          // Active states
          "active:scale-95 active:shadow-none",
          "active:border-[var(--checkbox-border-active-color,hsl(var(--primary)/0.9))]",
          "data-[state=checked]:active:bg-[var(--checkbox-bg-active-color,hsl(var(--primary)/0.8))]",
          // Focus states
          "focus-visible:ring-2 focus-visible:ring-[var(--checkbox-border-color,hsl(var(--primary)))] focus-visible:ring-opacity-50",
        ],
      },
      size: {
        default: "size-6 min-w-6 min-h-6 lg:size-5 lg:min-w-5 lg:min-h-5",
        sm: "size-4 min-w-4 min-h-4",
        lg: "size-7 min-w-7 min-h-7",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {
  routineColor?: string;
  theme?: "light" | "dark";
}

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, variant, size, routineColor, theme, style, ...props }, ref) => {
    // Generate CSS custom properties for routine variant
    const routineStyles = React.useMemo(() => {
      if (variant !== "routine" || !routineColor) return style;

      const [hue, saturation] = routineColor.split(" ");
      const isDark = theme === "dark";

      return {
        ...style,
        "--checkbox-border-color": `hsl(${hue} ${saturation} ${isDark ? "40%" : "50%"})`,
        "--checkbox-border-hover-color": `hsl(${hue} ${saturation} ${isDark ? "50%" : "40%"})`,
        "--checkbox-border-active-color": `hsl(${hue} ${saturation} ${isDark ? "60%" : "35%"})`,
        "--checkbox-bg-color": `hsl(${hue} ${saturation} ${isDark ? "50%" : "45%"})`,
        "--checkbox-border-checked-color": `hsl(${hue} ${saturation} ${isDark ? "60%" : "40%"})`,
        "--checkbox-bg-hover-color": `hsl(${hue} ${saturation} ${isDark ? "55%" : "40%"})`,
        "--checkbox-bg-active-color": `hsl(${hue} ${saturation} ${isDark ? "45%" : "50%"})`,
      } as React.CSSProperties;
    }, [variant, routineColor, theme, style]);

    return (
      <CheckboxPrimitive.Root
        ref={ref}
        className={cn(checkboxVariants({ variant, size }), className)}
        style={routineStyles}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          className={cn(
            "flex items-center justify-center text-current",
            "transition-transform duration-200 ease-in-out data-[state=checked]:scale-100 data-[state=unchecked]:scale-0",
          )}
        >
          <Check className={cn(variant === "routine" ? "size-3 @[576px]:size-4" : "size-4")} />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );
  },
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox, checkboxVariants };
